import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, FormControl, InputLabel, Select, TextField } from "@material-ui/core";
import { EditSimContext } from "../../context/EditSimContext";
import { UserContext } from "../../context/UserContext";
import { VariableContext } from "../../context/VariableContext";
import DashboardItemService from "../../services/DashboardItemService";
import Alert from "../generic/Alerts";
import { MIN_OPERATORS, MAX_OPERATORS, selectDashboardVariables } from "../../utils/dashboardItem";
import {
  NUMERIC_RANGE_VALUE_FIELDS,
  MAX_RANGE_VALUE_INPUT_LENGTH,
  initialDashboardRangeState,
  sanitizeIntegerRangeValue,
  withFormRangeValues,
  buildDashboardRangeApiPayload,
} from "../../utils/dashboardItemRangeForm";

const SimBuilderDashboardItemTab = () => {
  const { editSimState } = useContext(EditSimContext);
  const { token } = useContext(UserContext);
  const { variableState } = useContext(VariableContext);
  const [rangeState, setRangeState] = useState(initialDashboardRangeState);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertProps, setAlertProps] = useState({ msg: "", type: "" });

  useEffect(() => {
    if (!editSimState?.id) {
      return;
    }

    DashboardItemService()
      .getRangeBySim(editSimState.id, token)
      .then((response) => {
        if (response?.status === 200 && response?.data) {
          setRangeState({
            ...initialDashboardRangeState,
            ...response.data,
            ...withFormRangeValues(response.data),
            simId: editSimState.id,
          });
          return;
        }
        setRangeState((prev) => ({ ...prev, simId: editSimState.id }));
      })
      .catch(() => {
        setRangeState((prev) => ({ ...prev, simId: editSimState.id }));
      });
  }, [editSimState?.id, token]);

  const dashboardVariables = useMemo(
    () => selectDashboardVariables(variableState || []),
    [variableState]
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (NUMERIC_RANGE_VALUE_FIELDS.includes(name)) {
      setRangeState((prev) => ({ ...prev, [name]: sanitizeIntegerRangeValue(value) }));
      return;
    }
    setRangeState((prev) => ({ ...prev, [name]: value }));
  };

  const renderOperatorField = (name, label, options) => (
    <FormControl variant="outlined" fullWidth>
      <InputLabel htmlFor={name}>{label}</InputLabel>
      <Select native id={name} name={name} value={rangeState[name]} onChange={handleInputChange} label={label}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </FormControl>
  );

  const handleSave = () => {
    DashboardItemService()
      .addOrUpdateRange(buildDashboardRangeApiPayload(rangeState, editSimState?.id), token)
      .then((response) => {
        if (response?.status === 200) {
          if (response?.data) {
            setRangeState((prev) => ({
              ...prev,
              ...response.data,
              ...withFormRangeValues(response.data),
              simId: editSimState?.id,
            }));
          }
          setAlertProps({ msg: "Dashboard item settings saved successfully!", type: "success" });
        } else {
          setAlertProps({ msg: response?.message || "Unable to save dashboard item settings.", type: "error" });
        }
        setOpenAlert(true);
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to save dashboard item settings.";
        setAlertProps({ msg: errorMessage, type: "error" });
        setOpenAlert(true);
      });
  };

  const renderColorSection = (label, prefix) => (
    <div style={{ marginBottom: "8px" }}>
      <h5 style={{ margin: "4px 0" }}>{label}</h5>
      <div className="visibility_setting_row" style={{ marginBottom: "4px" }}>
        {renderOperatorField(`${prefix}MinOp`, "Min Operator", MIN_OPERATORS)}
        <TextField
          name={`${prefix}MinVal`}
          label="Min Value"
          variant="outlined"
          type="text"
          value={rangeState[`${prefix}MinVal`] ?? ""}
          onChange={handleInputChange}
          inputProps={{
            maxLength: MAX_RANGE_VALUE_INPUT_LENGTH,
            inputMode: "numeric",
            autoComplete: "off",
            "aria-label": "Min value (integer, max 15 characters)",
          }}
          fullWidth
        />
      </div>
      <div className="visibility_setting_row" style={{ marginBottom: "4px" }}>
        {renderOperatorField(`${prefix}MaxOp`, "Max Operator", MAX_OPERATORS)}
        <TextField
          name={`${prefix}MaxVal`}
          label="Max Value"
          variant="outlined"
          type="text"
          value={rangeState[`${prefix}MaxVal`] ?? ""}
          onChange={handleInputChange}
          inputProps={{
            maxLength: MAX_RANGE_VALUE_INPUT_LENGTH,
            inputMode: "numeric",
            autoComplete: "off",
            "aria-label": "Max value (integer, max 15 characters)",
          }}
          fullWidth
        />
      </div>
    </div>
  );

  return (
    <div className="variable_edit_form" style={{ paddingTop: "4px", paddingBottom: "4px" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "24px",
          alignItems: "flex-start",
        }}
      >
        <div style={{ flex: "0 0 220px", minWidth: "180px" }}>
          <h5 style={{ margin: "4px 0" }}>Dashboard Variables</h5>
          {dashboardVariables.length === 0 ? (
            <p style={{ margin: "4px 0", fontSize: "13px", color: "#777" }}>
              No variables flagged as dashboard items.
            </p>
          ) : (
            <ul style={{ margin: "0 0 6px 18px", padding: 0 }}>
              {dashboardVariables.map((variable) => (
                <li key={variable.id || variable.name}>{variable.name}</li>
              ))}
            </ul>
          )}
        </div>
        <div style={{ flex: "1 1 320px", minWidth: "280px" }}>
          {renderColorSection("Red", "red")}
          {renderColorSection("Yellow", "yellow")}
          {renderColorSection("Green", "green")}
        </div>
      </div>
      <Button variant="contained" color="primary" onClick={handleSave} style={{ marginTop: "8px" }}>
        Save
      </Button>
      {openAlert && <Alert {...alertProps} openAlert={openAlert} setOpenAlert={setOpenAlert} />}
    </div>
  );
};

export default SimBuilderDashboardItemTab;
